/**
 * Shared entry-data normalization helpers used by both `handleCapture` (single) and
 * `handleBatchCapture` (batch) in tools/smart-capture.ts, right before the
 * `chain.createEntry` call: value-emptiness checks (used to decide whether a
 * decomposed-content field should overwrite a user-supplied one) and select-field
 * canonicalization (fuzzy-match a supplied select value against the collection's
 * declared options). Extracted rather than inlined: smart-capture.ts is grandfathered
 * shrink-only under STD-2 / DEC-1504, so these additions are paid for here instead of
 * widening that file's baseline. Pure and self-contained — no dependency on the tool's
 * request/response shapes.
 */

/** Returns true if a value should be considered empty (null, empty array, or blank string). */
export function isEmptyValue(v: unknown): boolean {
  return v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === "");
}

/** Returns true if a value should be considered non-empty (opposite of isEmptyValue). */
export function isNonEmptyValue(v: unknown): boolean {
  return v != null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
}

/** Levenshtein distance for fuzzy select matching. DEC-173: fix when possible, never silently accept. */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

const APPETITE_OPTIONS = ["small", "medium", "large"] as const;

function findClosestOption(value: string, options: readonly string[]): { opt: string; dist: number } {
  const lower = value.toLowerCase();
  const exact = options.find((o) => o.toLowerCase() === lower);
  if (exact) return { opt: exact, dist: 0 };
  return options.reduce(
    (acc, opt) => {
      const d = levenshtein(lower, opt.toLowerCase());
      return d < acc.dist ? { opt, dist: d } : acc;
    },
    { opt: options[0], dist: Infinity },
  );
}

/**
 * Canonicalize select fields: if current value is not in options, find closest match.
 * Case-insensitive exact first, then Levenshtein distance. DEC-173: fix when possible.
 * BET-125 Slice 1: appetite (top-level before bet move), horizon, severity, priority from col.fields.
 */
export function canonicalizeSelects(
  data: Record<string, unknown>,
  fields: Array<{ key: string; type: string; options?: string[] }>,
  isBet: boolean,
): void {
  if (isBet && data.appetite != null && typeof data.appetite === "string") {
    const val = data.appetite.trim();
    if (val) {
      const { opt, dist } = findClosestOption(val, APPETITE_OPTIONS);
      if (dist <= 4) data.appetite = opt;
    }
  }

  for (const field of fields) {
    if (field.type !== "select" || !field.options?.length) continue;
    if (field.key === "appetite" && isBet) continue;
    const val = data[field.key];
    if (val == null || typeof val !== "string") continue;
    const trimmed = val.trim();
    if (!trimmed) continue;
    const { opt, dist } = findClosestOption(trimmed, field.options);
    if (dist <= 4) data[field.key] = opt;
  }
}
